import _ from 'lodash';
import moment from 'moment';
import DataSvc from './data';
import Formatters from './formatters';

export default {
  data() {
    return {
      requestActive: false,
    };
  },
  filters: {
    currency(number) {
      return Formatters.currency(number);
    },
    number(number, decimals) {
      return Formatters.number(number, decimals);
    },
    capitalize(str) {
      return _.startCase(str);
    },
  },
  computed: {
    registrationSession: {
      get() { return this.$root.$data.session; },
      set(session) { this.$root.$data.session = session; },
    },
    event: {
      get() { return this.$root.$data.evt; },
      set(evt) { this.$root.$data.evt = evt; },
    },
    events: {
      get() { return this.$root.$data.evts; },
      set(evts) { this.$root.$data.evts = evts; },
    },
    loggedInUser: {
      get() { return this.$root.$data.user; },
      set(user) { this.$root.$data.user = user; },
    },
    initPromise: {
      get() { return this.$root.$data.init; },
      set(init) { this.$root.$data.init = init; },
    },
    organizationID: {
      get() { return this.$root.$data.orgID; },
      set(orgID) { this.$root.$data.orgID = orgID; },
    },
    hiddenEvents: {
      get() { return this.$root.$data.hiddentEvts; },
      set(hiddentEvts) {
        this.localStorageSet('hiddenEvents', hiddentEvts);
        this.$root.$data.hiddentEvts = hiddentEvts;
      },
    },
    closedRegistrations: {
      get() { return this.$root.$data.crs; },
      set(crs) { this.$root.$data.crs = crs; },
    },
    balanceDueRegistrations() {
      return _.filter(this.closedRegistrations, rs => rs.balance_due > 0 && !rs.waitlist);
    },
    upcomingRegistrations() {
      return _.filter(this.closedRegistrations, rs => moment(rs.event.start_date_time).isAfter() && !rs.waitlist);
    },
    cartEmpty() {
      return (this.registrationSession.registrations || []).length === 0;
    },
    failedRegistration() {
      const states = _.map(this.registrationSession.registrations, 'state');
      return states.includes('failed');
    },
    isZeroDollarCart() {
      return this.registrationSession.amount_to_charge === 0;
    },
  },
  methods: {
    eventStatus(event) {
      const status = { canRegister: false };
      const openPricing = _.filter(
        event.pricing_packages,
        { is_open: true },
      );

      // closed due to no pricing
      if (openPricing.length <= 0) {
        status.canRegister = false;
        status.reason = 'Registration Closed (No Pricing)';

      // active roster open
      } else if (event.registration_open) {
        status.canRegister = true;
        status.reason = 'Registration Open (Active Roster)';

      // waitlist open
      } else if (event.waitlist_open) {
        status.canRegister = true;
        status.reason = 'Registration Open (Waitlist)';

      // closed due to full roster
      } else if (event.full_registrations
        && event.full_waitlist) {
        status.canRegister = false;
        status.reason = 'Registration Closed (Full)';

      // close due to event over
      } else if (event.event_over) {
        status.canRegister = false;
        status.reason = 'Registration Closed (Event Over)';

      // closed due to registration over
      } else {
        status.canRegister = false;
        status.reason = 'Registration Closed (Registration Over)';
      }

      return status;
    },
    newlineToBr(text) {
      return (text || '').replace(/\n/g, '<br>');
    },
    goToEvent(event) {
      const e = event || this.event;
      const hiddenCode = e.hidden ? e.hidden_code : null;

      this.$router.push({
        name: 'register',
        params: {
          eventID: e.id,
          hiddenCode,
        },
      });
    },
    goToCheckout() {
      this.$router.push({
        name: 'checkout',
      });
    },
    goToCart() {
      this.$router.push({ name: 'cart' });
    },
    checkCostScenario(unchangedSuccess) {
      if (!this.loggedInUser.id) return Promise.resolve();
      return this.updateActiveRegistrationSession()
        .then((data) => {
          let promise = Promise.resolve();

          if (data.costChange) {
            // warn
            this.showWarning(
              'Items in your cart have changed. Please review your cart before proceeding.',
              'Your cart has changed.',
            );

            promise = Promise.reject(new Error('Cart has changed. User must review changes.'));
          } else if (data.expiredPricingRegistrations.length > 0) {
            let msg = `
              One or more of your selected pricing options is no longer available.<br><br>
              Please remove and select new pricing option(s) for the following registration(s):<br><br>
            `;

            msg += _(data.expiredPricingRegistrations)
              .map(r => `&bull; ${r.registrant.full_name} - ${r.event.name} - ${r.pricing_package.label}`)
              .join('<br>');

            // warn
            this.showWarning(
              msg,
              'Registration pricing no longer available',
            );

            promise = Promise.reject(new Error('Pricing has expired. User must choose new pricing.'));
          } else if (unchangedSuccess) {
            promise = unchangedSuccess();
          }

          return promise;
        });
    },
    delay(t, val) {
      return new Promise((resolve) => {
        if (t <= 0) {
          resolve(val);
        } else {
          setTimeout(resolve.bind(null, val), t);
        }
      });
    },
    removeRegistration(id) {
      this.showGlobalLoader('Removing registrant...');
      this.requestActive = true;
      return DataSvc.deleteRegistration(
        this.registrationSession.uuid,
        id,
      ).then(this.updateActiveRegistrationSession)
        .finally(() => {
          this.hideGlobalLoader();
          this.requestActive = false;
        });
    },
    focusInput(ref) {
      this.$refs[ref].focus();
    },
    updateActiveRegistrationSession() {
      return DataSvc.getActiveRegistrationSession()
        .then((registrationSession) => {
          const currentSession = _.map(
            this.registrationSession.registrations,
            r => _.pick(r, [
              'state',
              'cost.total',
            ]),
          );

          const newSession = _.map(
            registrationSession.registrations,
            r => _.pick(r, [
              'state',
              'cost.total',
            ]),
          );

          const expiredPricingRegistrations = _.filter(
            registrationSession.registrations,
            r => !r.pricing_package.is_open,
          );

          const data = {
            costChange: JSON.stringify(currentSession) !== JSON.stringify(newSession),
            expiredPricingRegistrations,
          };

          // always set
          this.registrationSession = registrationSession;

          // return true/false if things have changed
          return data;
        });
    },
    updateClosedRegistrationSessions() {
      return DataSvc.getClosedRegistrationSessions()
        .then((closedRegistrations) => {
          this.closedRegistrations = closedRegistrations;
        });
    },
    clearLocalSession() {
      this.loggedInUser = {};
      this.registrationSession = {};
      this.hiddenEvents = [];
      this.closedRegistrations = [];
    },
    isUnauthorizedOnRoute(route) {
      const noAuthRoutes = ['events', 'register', 'cart'];
      return !this.loggedInUser.id && !noAuthRoutes.includes(route.name);
    },
    convertFloatDollarsToIntegerCents(floatDollars) {
      if (!floatDollars) {
        return 0;
      }

      return parseInt((parseFloat(floatDollars) * 100).toFixed());
    },
  },
};
